import type { ReactNode } from 'react'
import { Popover } from '@systemeio/components'
import { ChevronDownIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next/pages'
import { LocaleEnum, localeNames } from 'shared/enums/locale-enum'
import { useFloatHover } from 'shared/hooks/use-float-hover'
import { useLanguageSwitcher } from 'shared/hooks/use-language-switcher'
import { getCalculatedLanguage } from 'shared/utils/get-calculated-language'

const PublicLanguageSwitcher = ({ mobile }: { mobile?: boolean }): ReactNode => {
  const { i18n } = useTranslation()
  const { show, delayClose, delayOpen, close, open } = useFloatHover()
  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()

  if (!i18n) {
    return <div>loading...</div>
  }

  return (
    <Popover.Root
      open={show}
      onOpenChange={(next: boolean): void => {
        if (next) {
          open()
        } else {
          close()
        }
      }}
    >
      <div className="flex h-full items-center font-inter">
        <Popover.Trigger
          data-testid="templates-public-language-switcher-trigger"
          onMouseEnter={mobile ? undefined : delayOpen}
          onMouseLeave={mobile ? undefined : delayClose}
          disabled={isChangingLanguage}
          className={`flex size-fit items-center justify-around gap-2.5 text-text-primary outline-hidden focus-visible:focus-ring-primary ${
            isChangingLanguage ? 'animate-pulse' : ''
          }`}
        >
          <div className="w-5 text-sm font-semibold">{getCalculatedLanguage(i18n?.language)}</div>
          <ChevronDownIcon aria-hidden="true" className="size-5 text-icon-primary" />
        </Popover.Trigger>
      </div>
      <Popover.Portal>
        <Popover.Positioner
          side="bottom"
          sideOffset={8}
          collisionAvoidance={{ side: 'none', align: 'shift', fallbackAxisSide: 'none' }}
        >
          <Popover.Popup
            data-testid="templates-public-language-switcher-panel"
            onMouseEnter={mobile ? undefined : delayOpen}
            onMouseLeave={mobile ? undefined : delayClose}
            className={`max-h-(--available-height) max-w-[calc(100vw-1rem)] overflow-y-auto ${
              isChangingLanguage ? 'pointer-events-none' : ''
            }`}
          >
            <Popover.Arrow />
            <ul className="flex flex-col py-2 font-inter text-sm whitespace-nowrap">
              {Object.keys(localeNames).map(locale => (
                <li key={locale}>
                  <button
                    type="button"
                    className={`w-full cursor-pointer px-4 py-2 text-start text-text-secondary transition-colors hover:bg-bg-secondary hover:text-text-primary focus-visible:bg-bg-secondary focus-visible:text-text-primary focus-visible:outline-hidden ${
                      locale === i18n.language ? 'font-semibold text-text-primary' : ''
                    }`}
                    onClick={() => {
                      close()
                      void handleChangeLanguage(locale as LocaleEnum)
                    }}
                  >
                    {localeNames[locale as LocaleEnum]}
                  </button>
                </li>
              ))}
            </ul>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}

export default PublicLanguageSwitcher
